import React, { useState, useRef } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  Dimensions,
} from "react-native";
import { useNavigation } from "@react-navigation/native";
import { DEVICE_SIZES, useDeviceSize } from "rn-responsive-styles";
import SoloStreamingCard, { SoloStreamingCardProps } from "./SoloStreamingCard";
import { AppImages } from "../../utility/AppImages";
import { AppScreens } from "../../screens/Utility/AppScreens";

export interface LiveSoloStreamersProps {
  liveStreams: SoloStreamingCardProps[];
  onStreamPress?: (stream: SoloStreamingCardProps) => void;
  onUserPress?: (username: string) => void;
}

/**
 * LiveSoloStreamers - Section component with Live/Past tabs
 * Displays streaming cards in a horizontal scrollable layout
 */
const LiveSoloStreamers = ({
  liveStreams = [],
  onStreamPress,
  onUserPress,
}: LiveSoloStreamersProps) => {
  const scrollViewRef = useRef<ScrollView>(null);
  const deviceSize = useDeviceSize();
  const navigation = useNavigation();

  const isSmallDevice =
    deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;

  // Default navigation handler
  const handleStreamPress = (stream: SoloStreamingCardProps) => {
    if (onStreamPress) {
      onStreamPress(stream);
    } else {
      // Default behavior: navigate to solo streaming page
      (navigation as any).navigate(AppScreens.SoloStreaming, {
        username: stream.username,
      });
    }
  };

  // Scroll functions for navigation arrows
  const scrollLeft = () => {
    scrollViewRef.current?.scrollTo({
      x: -300,
      animated: true,
    });
  };

  const scrollRight = () => {
    scrollViewRef.current?.scrollTo({
      x: 300,
      animated: true,
    });
  };

  return (
    <View style={styles.container}>
      {/* Header Section */}
      <View
        style={[
          styles.headerContainer,
          isSmallDevice && styles.headerContainerMobile,
        ]}
      >
        <Text style={[styles.title, isSmallDevice && styles.titleSmall]}>
          Live solo Streamers
        </Text>

        <View style={styles.headerRight}>
          {/* Tab Buttons */}
          {/* Tab Buttons Removed */}
          <View style={styles.tabContainer}></View>

          {/* Navigation Arrows - Hidden on mobile */}
          {!isSmallDevice && (
            <View style={styles.arrowContainer}>
              <TouchableOpacity style={styles.arrowButton} onPress={scrollLeft}>
                <Text style={styles.arrowText}>←</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.arrowButton}
                onPress={scrollRight}
              >
                <Text style={styles.arrowText}>→</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </View>

      {/* Streaming Cards Scroll View */}
      <ScrollView
        ref={scrollViewRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={[
          styles.scrollContent,
          isSmallDevice && styles.scrollContentMobile,
        ]}
        style={styles.scrollView}
      >
        {liveStreams.length > 0 ? (
          liveStreams.map((stream, index) => (
            <View
              key={index}
              style={[
                styles.cardWrapper,
                index === 0 && styles.cardWrapperFirst,
                index === liveStreams.length - 1 && styles.cardWrapperLast,
              ]}
            >
              <SoloStreamingCard
                {...stream}
                width={isSmallDevice ? 280 : 356}
                onPress={() => handleStreamPress(stream)}
              />
            </View>
          ))
        ) : (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>No live streams available</Text>
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginVertical: 24,
  },
  headerContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 0,
    marginBottom: 20,
  },
  headerContainerMobile: {
    paddingHorizontal: 20,
  },
  title: {
    color: "#FFFFFF",
    fontSize: 24,
    fontWeight: "700",
  },
  titleSmall: {
    fontSize: 18,
  },
  headerRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  tabContainer: {
    flexDirection: "row",
    backgroundColor: "#1E1E2E",
    borderRadius: 8,
    padding: 4,
  },
  arrowContainer: {
    flexDirection: "row",
    gap: 8,
  },
  arrowButton: {
    width: 40,
    height: 40,
    backgroundColor: "#1E1E2E",
    borderRadius: 8,
    justifyContent: "center",
    alignItems: "center",
  },
  arrowText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "600",
  },
  scrollView: {
    width: "100%",
  },
  scrollContent: {
    paddingHorizontal: 0,
    flexGrow: 1,
  },
  scrollContentMobile: {
    paddingHorizontal: 20,
  },
  cardWrapper: {
    marginRight: 16,
  },
  cardWrapperFirst: {
    marginLeft: 0,
  },
  cardWrapperLast: {
    marginRight: 20,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 60,
    width: "100%",
  },
  emptyText: {
    color: "#999999",
    fontSize: 16,
  },
});

export default LiveSoloStreamers;
